/**
 * video-frame-proxy.js — window.VideoFrameProxy
 *
 * A picture for a file the browser cannot decode, WITHOUT converting it first.
 *
 * The tools that scrub (trim, frame pickers, compressors showing a poster) do
 * not need a continuous decoded stream — they need the frame at the timestamp
 * the user is pointing at. Measured on this WASM build, an AVI/MPEG-4 Part 2:
 *
 *   51 MB / 720p / 60 s   full conversion   165 765 ms
 *                         480p proxy         36 203 ms
 *                         THIS               173 ms once + 40–63 ms per frame
 *
 * `-ss` BEFORE `-i` is what makes it cheap: the demuxer jumps to the keyframe
 * near T instead of decoding everything up to it, so cost is flat in the file's
 * duration (measured 30 ms at 6 s and 30 ms at 54 s of the same clip).
 *
 * Why not just convert the file up front: for /video-trim it would also destroy
 * the tool's own promise — trimming an AVI with `-c copy` is lossless and
 * instant, and a user who asked for that should not silently receive a
 * re-encode plus a multi-minute wait before the workspace even opens.
 *
 * Usage:
 *   var px = VideoFrameProxy.create(file, { maxWidth: 854 });
 *   var url = await px.frameAt(12.5);     // object URL of a JPEG, or null
 *   px.dispose();                          // frees FS + every URL it handed out
 *
 * Scrubbing safety: FFmpeg-WASM is ONE instance and exec() is not re-entrant.
 * Requests are serialised, and while one runs only the LATEST queued timestamp
 * survives — a drag that crosses 40 positions costs two extracts, not forty.
 */
(function () {
'use strict';
if (typeof window === 'undefined') return;

var _seq = 0;

function create(file, opts) {
    opts = opts || {};
    var maxWidth = opts.maxWidth || 854;
    var id = 'vfp' + (++_seq) + '_' + Date.now();
    var inName = id + '_in';
    var inPath = inName;        // what goes after `-i` — a MEMFS name or a mount path
    var mount = null;           // {path, dispose} when the input is mounted, not copied
    var lane = opts.lane || (window.__VFP_LANE || 'auto');   // 'auto' | 'mount' | 'copy'
    var primed = null;          // Promise<bool>, resolved once
    var disposed = false;
    var cache = Object.create(null);   // rounded seconds -> object URL
    var urls = [];
    var running = false;
    var queued = null;          // {sec, resolve} — only the newest survives
    var reprimes = 0;           // the input re-mounted after the engine was replaced

    function key(sec) { return Math.round(sec * 4) / 4; }   // 0.25 s buckets

    function prime() {
        if (primed) return primed;
        primed = (async function () {
            if (!window.FFmpegProcessor) return false;
            try {
                if (!FFmpegProcessor.isReady()) await FFmpegProcessor.init();
            } catch (e) {
                try { console.warn('[frame-proxy] engine init failed:', e); } catch (_) {}
                return false;
            }
            /* Mount, don't copy. `mountInput` puts the File behind WORKERFS and
               FFmpeg reads slices on demand, so RAM no longer scales with the
               source — which is what the 400 MB ceiling below was guarding
               against. The copy lane stays as the fallback (and as the A/B arm
               the ceiling number came from). */
            if (lane !== 'copy' && typeof FFmpegProcessor.mountInput === 'function') {
                try {
                    mount = await FFmpegProcessor.mountInput(file, { name: inName });
                    if (disposed) { try { await mount.dispose(); } catch (_) {} mount = null; return false; }
                    inPath = mount.path;
                    return true;
                } catch (e) {
                    mount = null;
                    try { window.__ae && window.__ae('feature_use', {
                        slug: opts.slug || '', action: 'frame_proxy_mount_failed',
                        msg: ((e && e.message) || String(e || '')).substring(0, 120),
                    }); } catch (_) {}
                    if (lane === 'mount') return false;
                    /* fall through to the copy lane */
                }
            }
            try {
                var buf = await file.arrayBuffer();
                if (disposed) return false;
                await FFmpegProcessor._fs.write(inName, new Uint8Array(buf));
                inPath = inName;
                return true;
            } catch (e) {
                try { console.warn('[frame-proxy] prime failed:', e); } catch (_) {}
                return false;
            }
        })();
        return primed;
    }

    /* PNG out of FFmpeg, JPEG out of the browser (19.10.2026).
       This build's MJPEG encoder does not survive a second run in one engine:
       `-ss T -i in -frames:v 1 x.jpg` succeeds once and the next one dies with
       «RuntimeError: memory access out of bounds» — with or without the scale,
       `-threads 1`, `-update 1`, `format=yuvj420p`, from a mount or a copy, in
       Chrome 147 and 155 alike; a lavfi test pattern to .jpg traps too, while
       the same seeks to .png, to `-f null` and a .jpg WITHOUT -ss pass 4 of 4
       (tests/scratch lab, 19.10.2026). The trap discards the engine
       (_trapReset), the mounted input goes with it, and every later frame was
       «No such file»: on /video-trim the picture froze on the first frame while
       the person scrubbed — RU, 14 days: 277 sessions with a trap on exactly
       this command, a first frame in 361 of 643 sessions that needed the proxy.
       PNG costs 12–34 ms a frame here; the browser re-encodes it to a JPEG of
       the old size so the scrub cache does not grow tenfold. */
    async function compact(png) {
        if (typeof createImageBitmap !== 'function' || typeof window.__canvasBlob !== 'function') return png;
        try {
            var bmp = await createImageBitmap(png);
            var c = document.createElement('canvas');
            c.width = bmp.width; c.height = bmp.height;
            c.getContext('2d').drawImage(bmp, 0, 0);
            try { bmp.close(); } catch (_) {}
            var jpg = await window.__canvasBlob(c, 'image/jpeg', 0.85);
            return (jpg && jpg.size) ? jpg : png;
        } catch (_) { return png; }
    }

    async function extract(sec) {
        var out = id + '_' + Math.round(sec * 1000) + '.png';
        try {
            /* -ss ahead of -i is an INPUT seek. Put it after -i and FFmpeg
               decodes from zero to T, which turns a flat ~50 ms into
               something proportional to the timestamp. */
            var rc = await FFmpegProcessor._fs.exec([
                '-ss', Math.max(0, sec).toFixed(3), '-i', inPath,
                '-frames:v', '1',
                '-vf', "scale='min(" + maxWidth + ",iw)':-2", out
            ]);
            /* The engine this proxy primed is gone — a trap anywhere on the page
               discards it (_trapReset) and takes the mount along — so the input
               is «No such file» on the fresh one. Prime again (bounded) instead
               of answering null for the rest of the session. */
            if (rc && rc.code !== 0 && /No such file or directory/.test(String(rc.logs || '')) && reprimes < 3 && !disposed) {
                reprimes++;
                primed = null; mount = null; inPath = inName;
                try { window.__ae && window.__ae('feature_use', { slug: opts.slug || '', action: 'frame_proxy_reprime', n: reprimes }); } catch (_) {}
                if (await prime()) return extract(sec);
                return null;
            }
            var bytes = await FFmpegProcessor._fs.read(out);
            try { await FFmpegProcessor._fs.del(out); } catch (_) {}
            if (disposed || !bytes || !bytes.length) return null;
            var pic = await compact(new Blob([bytes], { type: 'image/png' }));
            if (disposed) return null;
            var url = URL.createObjectURL(pic);
            urls.push(url);
            return url;
        } catch (e) {
            try { await FFmpegProcessor._fs.del(out); } catch (_) {}
            return null;
        }
    }

    /* Resolves when no extract is running and none is queued. Polling, not a
       promise chain, because `queued` is deliberately overwritten by newer scrub
       positions — there is no single promise to await. */
    function whenIdle() {
        return new Promise(function (res) {
            (function check() {
                if (disposed || (!running && !queued)) return res();
                setTimeout(check, 40);
            })();
        });
    }

    function pump() {
        if (running || !queued || disposed) return;
        running = true;
        var job = queued; queued = null;
        extract(job.sec).then(function (url) {
            if (url) cache[key(job.sec)] = url;
            job.resolve(url);
        }, function () { job.resolve(null); })
         .then(function () { running = false; pump(); });
    }

    /* strip(opts) — the whole index in ONE pass, instead of one container open
       and one GOP decode per picture.

       Measured on this WASM build, 13 pictures, mounted input, arms back to
       back on the same page (tests/scratch/strip-bench, 2026-09-20):

         source                     per-slot exact   per-slot keyframe   ONE PASS
         136 MB / 130 s / HEVC          65.5 s            24.4 s          10.3 s
         585 MB / 180 s / HEVC          93.9 s            54.9 s          26.5 s
         1153 MB / 300 s / HEVC         98.3 s            58.9 s          50.8 s

       Two things do the work. `-skip_frame nokey` makes the decoder drop every
       non-key frame, and one `-i` means the container is opened once instead of
       `count` times. ⚠️ `-noaccurate_seek` is NOT the lever it looks like: on
       the same fixtures it measured 72.5 s against 65.5 s for plain exact seeks
       — no better, sometimes worse. And an `fps=` pass WITHOUT `-skip_frame`
       decodes everything: 165.5 s, the worst arm of the five.

       ⚠️ Returns FEWER pictures than asked — or none — when the source has
       fewer keyframes than the strip has slots (measured: a single-keyframe
       90 s file yields 0). That is not an error, it is the signal to fall back
       to per-slot seeks, which is the only arm that works there (252 s for the
       same 13 pictures — the cost of asking for exact times on such a file).

       Times are nominal slot times, not decoded timestamps: the pictures land
       on the fps grid, and a strip is an index, not a seek table. */
    async function strip(o) {
        o = o || {};
        var count = Math.max(1, o.count || 12);
        var w = o.frameWidth || 160;
        var dur = o.duration || 0;
        var onFrames = typeof o.onFrames === 'function' ? o.onFrames : null;
        if (disposed || !(dur > 0)) return [];
        var ok = await prime();
        if (!ok || disposed) return [];
        /* Join the queue, do not jump it. Both this pass and the player's own
           frame-at-0 await the same prime(), and without this the pass raced
           ahead into the (worker-serialised) engine: measured end to end on
           136 MB, the picture in the player arrived at 24.0 s instead of 10.5 s,
           because the user's ONE frame was waiting behind the whole index. */
        await whenIdle();
        if (disposed) return [];

        /* Chunks buy visible progress. One pass is the cheapest total time, but
           on a big source it means minutes of nothing and then everything at
           once. Measured, 13 pictures: 1153 MB — one pass 59.0 s, three chunks
           68.0 s; 2485 MB — 108.4 s against 120.5 s. ~15 % for a strip that
           fills in thirds, taken only where the wait is long enough to matter
           (≈500 MB ≈ 25 s). */
        var chunks = o.chunks || Math.max(1, Math.min(4, Math.round((file.size || 0) / (500 * 1024 * 1024))));
        var per = Math.ceil(count / chunks);
        var span = dur / chunks;
        var all = [];
        running = true;                     // hold the scrub queue for this pass
        try {
            for (var c = 0; c < chunks; c++) {
                if (disposed) break;
                var n = (c === chunks - 1) ? (count - per * c) : per;
                if (n <= 0) break;
                var stem = id + '_s' + (++_seq);
                var pre = chunks > 1
                    ? ['-skip_frame', 'nokey', '-ss', (c * span).toFixed(3), '-t', span.toFixed(3)]
                    : ['-skip_frame', 'nokey'];
                await FFmpegProcessor._fs.exec(pre.concat([
                    '-i', inPath, '-fps_mode', 'passthrough',
                    '-vf', 'fps=' + (n / span).toFixed(6) + ",scale='min(" + w + ",iw)':-2",
                    stem + '_%03d.png'
                ]));
                var batch = [];
                for (var i = 1; i <= n; i++) {
                    var name = stem + '_' + ('00' + i).slice(-3) + '.png';
                    var bytes = null;
                    try { bytes = await FFmpegProcessor._fs.read(name); } catch (_) { break; }
                    try { await FFmpegProcessor._fs.del(name); } catch (_) {}
                    if (!bytes || !bytes.length) break;
                    if (disposed) break;
                    var url = URL.createObjectURL(new Blob([bytes], { type: 'image/png' }));
                    urls.push(url);
                    batch.push({ slot: per * c + (i - 1), time: c * span + (i - 0.5) * (span / n), url: url });
                }
                all = all.concat(batch);
                if (batch.length && onFrames && !disposed) {
                    try { onFrames(batch); } catch (_) {}
                }
            }
        } catch (e) {
            try { console.warn('[frame-proxy] strip pass failed:', e); } catch (_) {}
        } finally {
            running = false;
            pump();
        }
        return disposed ? [] : all;
    }

    return {
        prime: prime,
        strip: strip,
        /* Resolves with an object URL for the frame at `sec`, or null. A
           superseded request resolves null rather than hanging — callers paint
           only what they get back, so a dropped scrub position is a no-op. */
        frameAt: function (sec) {
            if (disposed || !isFinite(sec)) return Promise.resolve(null);
            var k = key(sec);
            if (cache[k]) return Promise.resolve(cache[k]);
            return prime().then(function (ok) {
                if (!ok || disposed) return null;
                if (cache[k]) return cache[k];
                return new Promise(function (resolve) {
                    if (queued) { queued.resolve(null); }   // newest wins
                    queued = { sec: sec, resolve: resolve };
                    pump();
                });
            });
        },
        dispose: function () {
            disposed = true;
            if (queued) { queued.resolve(null); queued = null; }
            urls.forEach(function (u) { try { URL.revokeObjectURL(u); } catch (_) {} });
            urls = []; cache = Object.create(null);
            if (primed) primed.then(function (ok) {
                if (!ok) return;
                if (mount) { var m = mount; mount = null; try { m.dispose(); } catch (_) {} return; }
                try { FFmpegProcessor._fs.del(inName); } catch (_) {}
            });
        },
    };
}

window.VideoFrameProxy = { create: create };
})();
